import { useEffect, useEffectEvent, useRef, useState } from 'react';
import type { Nullable } from 'corgi-types';

const MAX_INFINITE_SCROLL_COUNT = 3;

interface UseInfiniteScrollProps {
  isLoadMoreObserved: boolean;
  hasMoreProductsToLoad: boolean;
  isLoading: boolean;
  offset: number;
  onFetchMore: () => void;
}

export const useInfiniteScroll = ({
  isLoadMoreObserved,
  hasMoreProductsToLoad,
  isLoading,
  offset,
  onFetchMore,
}: UseInfiniteScrollProps) => {
  const lastRequestedOffset = useRef<Nullable<number>>(null);
  const [infiniteScrollCount, setInfiniteScrollCount] = useState(0);

  const shouldUseInfiniteScroll = infiniteScrollCount < MAX_INFINITE_SCROLL_COUNT;

  const handleFetchMoreWithCounter = () => {
    setInfiniteScrollCount(prev => prev + 1);
    onFetchMore();
  };

  const onAutoFetchMore = useEffectEvent(handleFetchMoreWithCounter);

  useEffect(() => {
    if (shouldUseInfiniteScroll && isLoadMoreObserved && hasMoreProductsToLoad && !isLoading) {
      if (lastRequestedOffset.current === offset) {
        return;
      }

      lastRequestedOffset.current = offset;
      onAutoFetchMore();
    }
  }, [shouldUseInfiniteScroll, isLoadMoreObserved, hasMoreProductsToLoad, isLoading, offset]);

  return {
    shouldUseInfiniteScroll,
    infiniteScrollCount,
    handleFetchMoreWithCounter,
  };
};
